import {HttpClient, HttpHeaders} from '@angular/common/http';
import {inject} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {CanActivateFn, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {
  ConfigService,
  DialogWidth,
  handleError,
  SessionService,
  SnackBarService,
  ThemePalette
} from '@q9elements/ui-kit/common';
import {get} from 'lodash';
import {catchError, finalize, map, switchMap} from 'rxjs';

import {SfConnectionInfoDialogComponent} from '../dialogs/sf-connection-info/sf-connection-info-dialog.component';
import {RefreshSfTokenResponse, SfCallbackParams} from '../models/salesforce-orgs.interface';
import {sfCallbackParser} from '../utils/sf-callback-parser';
import {getAndRemovePkceVerifierByState} from '../utils/sf-pkce-storage';

export const SalesforceRefreshCallbackGuard: CanActivateFn = route => {
  const translateService = inject(TranslateService);
  const router = inject(Router);
  const http = inject(HttpClient);
  const snackBarService = inject(SnackBarService);
  const dialog = inject(MatDialog);
  const sessionService = inject(SessionService);
  const parsedSfQueryParams = sfCallbackParser(route.queryParams as SfCallbackParams);
  const state = String(route.queryParams['state'] ?? '');
  const teamId = parsedSfQueryParams.teamId ?? String(sessionService.teamId());
  const teamIndex = parsedSfQueryParams.teamIndex ?? String(sessionService.teamIndex());
  const payload = {
    code: parsedSfQueryParams.code,
    isSandbox: parsedSfQueryParams.isSandbox,
    orgId: parsedSfQueryParams.orgId,
    codeVerifier: getAndRemovePkceVerifierByState(state)
  };

  const headers = new HttpHeaders({'el-team-id': String(teamId), 'el-team': String(teamIndex)});
  const url = `${ConfigService.environment.API_ENDPOINT}/organizations/${teamId}/salesforce/token`;
  const returnBackToReferenceModels = (
    queryParams: {orgId?: string; message?: string} | object = {}
  ) => router.navigate([`/t/${+teamIndex}/referencemodels-external`], {queryParams});

  const openSfConnectionInfoDialog = (data: RefreshSfTokenResponse) => {
    return dialog
      .open(SfConnectionInfoDialogComponent, {
        width: DialogWidth.MD,
        disableClose: !data.isReconnect,
        data: {...data, color: ThemePalette.PRIMARY}
      })
      .afterClosed()
      .pipe(finalize(() => sessionService.setLoading(false)));
  };

  sessionService.setLoading();

  return http.post<RefreshSfTokenResponse>(url, payload, {headers}).pipe(
    handleError(),
    map(response => {
      const params: {orgId?: string; message?: string} = {};

      if (response.orgReconnected) {
        params.message = translateService.instant('SF_CONNECTIONS.SANDBOX_RECONNECTED');
      }

      if (response.tokenUpdated) {
        params.message = translateService.instant('SF_CONNECTIONS.TOKEN_REFRESHED');
      }

      if (!response.alreadyConnected) {
        params.orgId = response.orgId;
      }

      return {response, params};
    }),
    switchMap(({response, params}) => {
      const {startSync, refModelId, type} = parsedSfQueryParams;
      const syncType = type || 'external';
      const shouldStartSync = startSync === 'true' && !!refModelId;

      if (shouldStartSync) {
        const syncUrl = `${ConfigService.environment.API_ENDPOINT}/refmodel/${syncType}/structure/sync/${refModelId}`;

        http.post(syncUrl, null, {headers}).pipe(handleError()).subscribe();

        return returnBackToReferenceModels(params);
      }

      if (!response.alreadyConnected) {
        return returnBackToReferenceModels(params);
      }

      return openSfConnectionInfoDialog(response).pipe(
        switchMap(() => returnBackToReferenceModels(params))
      );
    }),
    finalize(() => sessionService.setLoading(false)),
    catchError(err => {
      snackBarService.open(get(err, 'error', err));

      return returnBackToReferenceModels();
    })
  );
};
